import { DateTime } from 'luxon'
import { head, trimEnd } from 'lodash-es'
import Papa from 'papaparse'
import privateStorage from '@/store/privateStorage.js'
import { StatusBar } from '@capacitor/status-bar'
import { Capacitor } from '@capacitor/core'
import { getOrgPublicJsonPath } from '../../../api/common/sharedUtils.js'
import markdownit from 'markdown-it'
import api from '@/service/Api.js'
import eventBus from '@/service/EventBus.js'
import djBigSmile from '@/assets/daily-journal/big-smile.png'
import djHappyFace from '@/assets/daily-journal/happy-face.png'
import djNeutralFace from '@/assets/daily-journal/neutral-face.png'
import djFrownFace from '@/assets/daily-journal/frown-face.png'
import djTearyFace from '@/assets/daily-journal/teary-face.png'
import djUnknownFace from '@/assets/daily-journal/unknown-face.png'

function getDjEmojiResource({ emojiFileName }) {
    switch (emojiFileName) {
        case 'big-smile.png':
            return djBigSmile
        case 'happy-face.png':
            return djHappyFace
        case 'neutral-face.png':
            return djNeutralFace
        case 'frown-face.png':
            return djFrownFace
        case 'teary-face.png':
            return djTearyFace
        case 'unknown-face.png':
            return djUnknownFace
    }
    return djUnknownFace
}

function getCompactDate({ isoString }) {
    const inputDate = DateTime.fromISO(isoString)
    return inputDate.toFormat('M\u2011d\u2011yyyy') // Example: "12-01-2024, 3:45 PM"
}

function getHumanFriendlyDate({ isoString }) {
    const inputDate = DateTime.fromISO(isoString)
    const now = DateTime.now()

    if (inputDate.hasSame(now, 'day')) {
        return inputDate.toFormat('h:mm a') // Example: "3:45 PM"
    }
    if (inputDate.hasSame(now.minus({ days: 1 }), 'day')) {
        return `Yesterday, ${inputDate.toFormat('h:mm a')}` // Example: "Yesterday, 3:45 PM"
    }
    if (inputDate > now.minus({ days: 6 })) {
        return inputDate.toFormat('cccc, h:mm a') // Example: "Saturday, 3:45 PM"
    }
    return inputDate.toFormat('MMM d, yyyy, h:mm a') // Example: "Dec 1, 2024, 3:45 PM"
}

function encodeBase64(input) {
    const bytes = new TextEncoder().encode(input)
    let binary = ''
    bytes.forEach(b => binary += String.fromCharCode(b))
    return btoa(binary)
}

function decodeBase64(encoded) {
    const binary = atob(encoded)
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))
    return new TextDecoder().decode(bytes)
}

function idToLinkId({ id }) {
    return encodeBase64(id)
}

function linkIdToId({ linkId }) {
    return decodeBase64(linkId)
}

function base64ImageToBlob({ base64ImgData }) {
    const base64Data = base64ImgData.split(',')[1]
    const binaryData = new Uint8Array([...atob(base64Data)].map(c => c.charCodeAt(0)))
    return new Blob([binaryData], { type: 'image/jpeg' })
}

function getInitials({ firstName, lastName }) {
    return [firstName, lastName].filter(e => !!e).map(head).join('')
}

const requiredRule = v => !!v || 'Required'

const phoneRule = v => /^\d{3}-\d{3}-\d{4}$/.test(v) || 'required format: ###-###-####'

const dobRule = v => (
    !v ||
    /^\d{2}-\d{2}-\d{4}$/.test(v) ||
    /^\d{2}\/\d{2}\/\d{4}$/.test(v)
) || 'Use format mm-dd-yyyy or mm/dd/yyyy'

function toBackendDobFormat(dob) {
    if (!dob) return null
    const [mm, dd, yyyy] = dob.split(/[/\-]/)
    return [yyyy, mm, dd].join('-')
}

function toFrontendDobFormat(dob) {
    if (!dob) return null
    const [yyyy, mm, dd] = dob.split(/[/\-]/)
    return [mm, dd, yyyy].join('-')
}

const genders = ['Male', 'Female', 'Non-binary', 'Rather not say']

const SystemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

async function userPrefersDarkMode() {
    const lsTheme = await privateStorage.getItem('kn.theme')
    return lsTheme
        ? lsTheme === 'dark' || (lsTheme === 'system' && SystemPrefersDark)
        : SystemPrefersDark
}

async function setNativeAppStatusBarPurple() {
    if (!Capacitor.isNativePlatform()) return
    await StatusBar.setBackgroundColor({ color: '#4e49d2' })
}

async function setNativeAppStatusBarColor(themeOverride) {
    if (!Capacitor.isNativePlatform()) return
    const darkBg = '#212121ff'
    const lightBg = '#ffffff'
    if (themeOverride) {
        await StatusBar.setBackgroundColor({ color: themeOverride === 'dark' ? darkBg : lightBg })
        return
    }
    const prefDark = await userPrefersDarkMode()
    await StatusBar.setBackgroundColor({ color: prefDark ? darkBg : lightBg })
}

function profileImageUrl(objWithUrlOrProfileImageKey) {
    if (
        !objWithUrlOrProfileImageKey?.profileImageKey &&
        !objWithUrlOrProfileImageKey?.url
    ) return null
    if (objWithUrlOrProfileImageKey?.url) return objWithUrlOrProfileImageKey.url

    const env = process.env.KN_ENV
    return `https://${env}.kinectin.com/${objWithUrlOrProfileImageKey.profileImageKey}`
}

function s3ContentUrl(s3Key) {
    return `https://${process.env.KN_ENV}.kinectin.com/${s3Key}`
}

async function uploadOrgS3Content({ orgId, fileName, filePrefix, fileSuffix, contentType, body, usePrivateBucket }) {
    const { response: { url, key } } = await api.getOrgS3UploadUrl({
        orgId,
        fileName,
        filePrefix,
        fileSuffix,
        contentType,
        usePrivateBucket,
    })
    const uploadResponse = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body })
    if (!uploadResponse.ok) {
        const err = 'Upload failed :('
        eventBus.spa.emit('show-snackbar', { text: err, color: 'red' })
        return
    }
    return key
}

function handleMdClick(event, router) {
    const target = event.target
    if (target.tagName === 'A') {
        const href = target.getAttribute('href')
        // Check if it's an internal link (starts with '/')
        if (href && href.startsWith('/') && !href.startsWith('//')) {
            event.preventDefault() // Prevent full page reload
            router.push(href) // Use Vue Router navigation
        }
    }
}

async function loadCsv({ url }) {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Failed to fetch CSV: ${response.statusText}`)
    const csvText = await response.text()
    const { data, errors, meta } = Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
    })
    return data
}

function telify(text) {
    if (!text?.length) return text
    const phoneRegex = /(?:\+?\d{1,3}[ .-]?)?(?:\(\d{3}\)|\d{3})[ .-]?\d{3}[ .-]?\d{4}|\b(911|988)\b/g
    return text.replace(phoneRegex, match => {
        // Strip out everything except digits and '+' for the href
        const hrefNumber = match.replace(/[^+\d]/g, '')
        return `<a href="tel:${hrefNumber}">${match}</a>`
    })
}

const md = markdownit({
    breaks: true,
    html: true,
    linkify: true,
    typographer: true,
})

function renderMessage(message) {
    const replaceNewlines = str => str.replaceAll('\n', '<br>')
    return replaceNewlines(trimEnd(telify(md.render(message.content))))
}

async function fetchPublicJson({ urlSegment }) {
    const urlPath = getOrgPublicJsonPath({ urlSegment })
    const response = await fetch(`https://${process.env.KN_ENV}.kinectin.com/${urlPath}`)
    if (!response.ok) {
        throw Error(`error fetching org data... status: ${response.status}`)
    }
    return await response.json()
}

function addGoogleAnalyticsToHead() {
    const gaMid = process.env.GA_MEASUREMENT_ID
    if (!gaMid) return

    // 1. Load the external gtag.js script
    const loader = document.createElement('script')
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${gaMid}`
    loader.async = true
    document.head.appendChild(loader)

    // 2. Inject the inline configuration
    const inline = document.createElement('script')
    inline.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaMid}', { send_page_view: true });`
    document.head.appendChild(inline)
}

async function setSignUpStorage({ query, orgId }) {
    const { redirect } = query
    if (redirect && redirect !== '/') await privateStorage.setItem('kn.redirect', '' + redirect)

    if (orgId) {
        await privateStorage.setItem('kn.orgIdSignUp', orgId)
        await privateStorage.removeItem('kn.signUp')
    } else {
        await privateStorage.setItem('kn.signUp', 'true')
        await privateStorage.removeItem('kn.orgIdSignUp')
    }
}

async function clearSignUpStorage() {
    await privateStorage.removeItem('kn.redirect')
    await privateStorage.removeItem('kn.orgIdSignUp')
    await privateStorage.removeItem('kn.signUp')
}

function userNameOrId(item) {
    return item.firstName ? `${item.firstName} ${item.lastName}` : item.userId || '(no userId)'
}

function orgLink(org) {
    return `/org/${idToLinkId({ id: org.orgId })}`
}

async function getLastOnboardAge() {
    const lastTsStr = await privateStorage.getItem('kn.onboarding.lastTimestamp') || '1971-01-01T00:00:00.000Z'
    const lastTs = DateTime.fromISO(lastTsStr)
    const now = DateTime.now()
    const hours = now.diff(lastTs, 'hours').days.toFixed()
    return { hours }
}

async function getLastOnboardBaselineEntries() {
    const { hours } = await getLastOnboardAge()
    if (hours >= 2) await privateStorage.removeItem('kn.onboarding.baselineEntries')
    const withB64 = true
    const res = await privateStorage.getItemObj('kn.onboarding.baselineEntries', withB64) || []
    const month = DateTime.now().toFormat('yyyy-MM')
    res.map(e => e.month = month)
    return res
}

async function updateOnboardBaselineEntries({ scores, analysisResult, done }) {
    await privateStorage.setItem('kn.onboarding.lastTimestamp', DateTime.now().toUTC().toString())
    const withB64 = true
    await privateStorage.setItemObj('kn.onboarding.baselineEntries', [{ scores, analysisResult, done }], withB64)
}

async function removeOnboardBaselineEntries() {
    await privateStorage.removeItem('kn.onboarding.lastTimestamp')
    await privateStorage.removeItem('kn.onboarding.baselineEntries')
}

export {
    encodeBase64,
    decodeBase64,
    removeOnboardBaselineEntries,
    updateOnboardBaselineEntries,
    getLastOnboardBaselineEntries,
    getLastOnboardAge,
    getDjEmojiResource,
    getHumanFriendlyDate,
    getCompactDate,
    idToLinkId,
    linkIdToId,
    getInitials,
    requiredRule,
    dobRule,
    phoneRule,
    toBackendDobFormat,
    toFrontendDobFormat,
    genders,
    userPrefersDarkMode,
    setNativeAppStatusBarPurple,
    setNativeAppStatusBarColor,
    SystemPrefersDark,
    base64ImageToBlob,
    profileImageUrl,
    s3ContentUrl,
    uploadOrgS3Content,
    handleMdClick,
    loadCsv,
    telify,
    fetchPublicJson,
    addGoogleAnalyticsToHead,
    setSignUpStorage,
    clearSignUpStorage,
    userNameOrId,
    orgLink,
    renderMessage,
}